import React, { MouseEvent, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { maxCharOfComparisonTable } from '../constants';
import { IEntry, IFormField, IFormStructure } from '../schema/interface';
import { getDate, getDateTime } from '../utils/date';
import FieldDetailDialog from './Dialog/FieldDetail';

const styles = createUseStyles({
    container: {
        // height: '100%',
    },
    tableContainer: {
        margin: '13px',
        maxWidth: '100%',
        overflow: 'auto'
    },
    title: {
        fontSize: '17px',
        fontWeight: '600'
    },
    date: {
        borderBottom: '1px solid #545e66',
        paddingBottom: '2px',
        fontWeight: 'bold',
        '&:hover': {
            borderColor: '#509ffa',
            color: '#509ffa'
        }
    },
    table: {
        background: 'white',
        borderSpacing: '0',
        color: '#232733',
        fontSize: '13px',
        fontWeight: '500',
        maxWidth: '100%',
        overflow: 'auto',
        textAlign: 'center',
        '& td': {
            minWidth: '100px',
            maxWidth: '220px',
            padding: '15px',
            textAlign: 'center'
        },
        '& td div': { textAlign: 'center' },
        '& th': {
            minWidth: '100px',
            maxWidth: '180px',
            padding: '15px',
            color: '#545e66'
        }
    },
    tableHeader: {
        textAlign: 'center',
        '& th': {
            borderBottom: 'solid 1.5px rgba(206, 210, 214, 0.8)',
            cursor: 'pointer',
            '&:first-child': {
                borderRight: 'solid 1.5px rgba(206, 210, 214, 0.8)',
                textAlign: 'right',
                position: 'sticky',
                left: '0',
                zIndex: '2',
                background: 'white'
            }
        }
    },
    tableBody: {
        '& th': {
            borderRight: 'solid 1.5px rgba(206, 210, 214, 0.8)',
            textAlign: 'right',
            position: 'sticky',
            left: '0',
            zIndex: '2',
            background: 'inherit'
        },
        '& tr': {
            minHeight: '54px',
            '& th:first-child': {
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px'
            },
            '& td:last-child': {
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px'
            },
            '& td, & th': {
                background: 'white'
            },
            '&:nth-of-type(even)': {
                '& td, & th': {
                    background: '#e8ecf2'
                }
            }
        }
    },
    emptyTip: {
        textAlign: 'center'
    },
    valueContainer: {
        textAlign: 'start'
    },
    number: {
        textAlign: 'center'
    },
    ellipsed: {
        cursor: 'pointer'
    }
});
interface FormComparisonProp {
    entries: IEntry[];
    formStructure: IFormStructure;
    isGregorian?: boolean;
    handleSelectEntry: (formEntryId: number) => {};
}

const FormComparison = ({
    entries,
    formStructure,
    isGregorian,
    handleSelectEntry
}: FormComparisonProp) => {
    let fields = formStructure?.fields ? JSON.parse(JSON.stringify(formStructure?.fields)) : [];
    fields = fields
        ?.filter(
            (val: IFormField) =>
                val.data_type !== 'none' &&
                val.data_type !== 'file' &&
                val.data_type !== 'group' &&
                !val?.presentation?.invisible &&
                val?.label &&
                !val?.hidden
        )
        .sort((a, b) => a.order - b.order);

    const classes = styles();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [selectedField, setSelectedField] = useState(null);
    useEffect(() => {
        window.addEventListener('click', handleCloseDetailDialog);
        return () => {
            window.removeEventListener('click', handleCloseDetailDialog);
        };
    }, []);
    if (!formStructure || !entries) {
        return null;
    }
    const renderFormValue = (field: IFormField, entry: IEntry) => {
        const dataType = field.data_type;
        let resultString = '';
        const value = entry?.content?.[field.name];
        if (!value) {
            return null;
        }
        if (dataType === 'select') {
            if (typeof value === 'string') {
                resultString = value;
            } else {
                const choices = value?.choices?.length;
                const others = value?.others?.length;
                if (choices) {
                    typeof value?.choices[0] === 'string'
                        ? value?.choices?.forEach?.((item: string, index: number) => {
                              resultString += item;
                              resultString += index < choices - 1 ? ', ' : '';
                          })
                        : value?.choices?.forEach?.((item: string[], index: number) => {
                              resultString += item[1];
                              resultString += index < choices - 1 ? ', ' : '';
                          });
                } else if (others) {
                    value?.others?.forEach?.((item: string) => {
                        resultString += item;
                    });
                }
            }
        } else if (dataType === 'date') {
            resultString = getDate(value, !isGregorian);
            return <div className={classes.number}>{resultString}</div>;
        } else if (dataType === 'datetime') {
            resultString = getDateTime(value, !isGregorian);
            return <div className={classes.number}>{resultString}</div>;
        } else if (dataType === 'integer' || dataType === 'float') {
            return <div className={classes.number}>{value}</div>;
        }
        // else if (dataType === 'file') {
        //   const extension = value.slice(value.lastIndexOf('.') + 1);
        //   if (IMAGE_FILE_EXTENSIONS.includes(extension)) {
        //     return <img src={value} alt={field.name} width={'75px'} />;
        //   } else {
        //     resultString = value;
        //   }
        else {
            resultString = value;
        }
        if (resultString.length > maxCharOfComparisonTable) {
            const subStrResult = `${resultString.substring(0, maxCharOfComparisonTable - 1)} ..`;
            return (
                <FieldDetailDialog
                    isOpen={
                        openDialog && selectedEntry === entry?.id && selectedField === field?.name
                    }
                    fieldValue={resultString}
                    TargetRef={(ref) => (
                        <div
                            ref={ref}
                            dir="auto"
                            className={classes.ellipsed}
                            onClick={(e) => handleOpenDetailDialog(e, entry?.id, field?.name)}
                        >
                            {subStrResult}
                        </div>
                    )}
                />
            );
        }
        return (
            <div dir="auto" className={classes.valueContainer}>
                {resultString}
            </div>
        );
    };
    const handleOpenDetailDialog = (e: MouseEvent, entryId: number, fieldName: string) => {
        e.stopPropagation();
        setSelectedEntry(entryId);
        setSelectedField(fieldName);
        setOpenDialog(true);
    };
    const handleCloseDetailDialog = () => {
        setOpenDialog(false);
        setSelectedEntry(null);
        setSelectedField(null);
    };
    return (
        <div className={classes.container}>
            {entries.length ? (
                <div className={classes.tableContainer}>
                    <table className={classes.table}>
                        <thead className={classes.tableHeader}>
                            <tr>
                                <th aria-label="th" />
                                {[...entries]
                                    ?.sort(
                                        (a, b) =>
                                            new Date(a?.created)?.getTime() -
                                            new Date(b?.created)?.getTime()
                                    )
                                    .map((entry, index) => (
                                        <th
                                            key={index}
                                            onClick={() => handleSelectEntry?.(entry.id)}
                                        >
                                            <span className={classes.date}>
                                                {getDate(entry.created, !isGregorian)}
                                            </span>
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody className={classes.tableBody}>
                            {fields.map((field: IFormField) => (
                                <tr key={field.name}>
                                    <th>{field.label}</th>
                                    {entries?.map((entry: IEntry, index) => (
                                        <td key={index}>{renderFormValue(field, entry)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={classes.emptyTip}>No Entry has been submitted yet!</div>
            )}
        </div>
    );
};
export default FormComparison;
